import { TimeCacheMap } from '@/utils/cache-map';

interface SearchLibCompanyData {
  name: string;
  exchange: string;
  symbol: string;
};

function isSearchLibCompanyData(unknownValue: unknown): unknownValue is SearchLibCompanyData {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'name' in unknownValue &&
    'exchange' in unknownValue &&
    'symbol' in unknownValue &&
    typeof unknownValue.name === 'string' &&
    typeof unknownValue.exchange === 'string' &&
    typeof unknownValue.symbol === 'string'
  );
}

interface AutoSuggestResponse {
  data: SearchLibCompanyData[];
};

function isAutoSuggestResponse(unknownValue: unknown): unknownValue is AutoSuggestResponse {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'data' in unknownValue &&
    Array.isArray(unknownValue.data) &&
    unknownValue.data.every(isSearchLibCompanyData)
  );
}

interface FetchSymbolOptions {
  searchQuery: string;
  maxResults?: number;
  sanitize: boolean;
};

interface OptionsWithSanitize extends FetchSymbolOptions {
  sanitize: true;
};

interface OptionsWithoutSanitize extends FetchSymbolOptions {
  sanitize: false;
};

export interface AutoSuggestEntry {
  symbol: string;
  company: string;
  exchange: string;
};

const MAX_RESULT_FALLBACK = 10;
async function useFetchSymbolSuggestions(options: OptionsWithoutSanitize): Promise<SearchLibCompanyData[]>;
async function useFetchSymbolSuggestions(options: OptionsWithSanitize): Promise<AutoSuggestEntry[]>;
async function useFetchSymbolSuggestions(options: FetchSymbolOptions): Promise<AutoSuggestEntry[] | SearchLibCompanyData[]> {
  const fallbackOptions = {
    maxResults: MAX_RESULT_FALLBACK,
    ...options
  };

  const { searchQuery, maxResults } = fallbackOptions;

  const urlSearchParams = new URLSearchParams({
    section: 'symbols',
    q: searchQuery,
    perPage: maxResults.toString()
  });

  let responseJson;

  try {
    const response = await fetch(`/quotebrain/search?${urlSearchParams.toString()}`, {
      method: 'GET'
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info(`symbol suggestions responseJson for query: ${searchQuery}`, responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch symbol suggestions for query: ${searchQuery}.`, e);

    throw new Error(`Failed to fetch symbol suggestions for query: ${searchQuery}.`, { cause: e });
  }

  if (!isAutoSuggestResponse(responseJson)) {
    throw new Error(`Failed to fetch symbol suggestions for query: ${searchQuery} - responseJson does not match AutoSuggestResponse interface: ${typeof responseJson} ${responseJson}.`);
  }

  const rawSuggestions = responseJson.data;

  if (!fallbackOptions.sanitize) {
    return rawSuggestions;
  }

  return rawSuggestions.filter((rawSuggestion: SearchLibCompanyData) => {
    return (
      !!rawSuggestion.symbol.trim() &&
      !!rawSuggestion.name.trim() &&
      !!rawSuggestion.exchange.trim()
    );
  }).map((suggestion: SearchLibCompanyData) => {
    return {
      symbol: suggestion.symbol.trim().toUpperCase(),
      company: suggestion.name.trim(),
      exchange: suggestion.exchange.trim().toUpperCase()
    };
  }).slice(0, fallbackOptions.maxResults);
}

const MINUTE = 60 * 1000;
const REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY = 1 * MINUTE;
const EXPIRE_AFTER_FREQUENCY = 5 * MINUTE;

const keyStringCache = new TimeCacheMap<AutoSuggestEntry[]>(REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY, EXPIRE_AFTER_FREQUENCY);

async function getSuggestions(keyString: string): Promise<AutoSuggestEntry[]> {
  const formattedString = keyString.toUpperCase().trim();

  const cachedData = keyStringCache.get(formattedString);

  if (cachedData) {
    return cachedData;
  }

  let data: AutoSuggestEntry[] = [];

  try {
    data = await useFetchSymbolSuggestions({
      searchQuery: formattedString,
      maxResults: MAX_RESULT_FALLBACK,
      sanitize: true
    });
  } catch (error) {
    window.sc3WorkbenchLogger.error(`Error while fetching symbol suggestions in getSuggestions for symbol: ${formattedString}`, error);

    return data;
  }

  keyStringCache.set(formattedString, data);

  return data;
}

interface StringMatch {
  value: string;
  matched: boolean;
};

function matchSubstringToString(substring: string, mainString: string): StringMatch[] {
  const matchedIndices = [...mainString.matchAll(new RegExp(substring, 'gi'))]
    .map(regex => regex.index)
    .filter(index => typeof index === 'number');

  const parts: StringMatch[] = [];
  let last = 0;

  for (const index of matchedIndices) {
    const nonMatchedSection = mainString.slice(last, index);
    const matchedSection = mainString.slice(index, index + substring.length);

    if (index !== last) {
      parts.push({ value: nonMatchedSection, matched: false });
    }

    parts.push({ value: matchedSection, matched: true });

    last = index + substring.length;
  }

  parts.push( { value: mainString.slice(last), matched: false } );

  return parts;
}

function wrapMatchedStringsInBold(subStrings: StringMatch[]) {
  return subStrings.map(subString => {
    return subString.matched ? `<b>${subString.value}</b>` : subString.value;
  }).join('');
}

interface SuggestionHtml extends AutoSuggestEntry {
  highlightedCompany: string;
  highlightedSymbol: string;
};

type HighlightWrapFunction = (subStrings: StringMatch[]) => string;

function getHighlightedHtmlFromComparison(comparisonString: string, comparedEntries: AutoSuggestEntry[], wrapFunction: HighlightWrapFunction = wrapMatchedStringsInBold) {
  return comparedEntries.map<SuggestionHtml>(suggestion => {
    return {
      ...suggestion,
      highlightedSymbol: wrapFunction(matchSubstringToString(comparisonString, suggestion.symbol)),
      highlightedCompany: wrapFunction(matchSubstringToString(comparisonString, suggestion.company))
    }
  });
}

export function useSymbolSuggestions() {
  return {
    getSuggestions,
    matchSubstringToString,
    wrapMatchedStringsInBold,
    getHighlightedHtmlFromComparison
  };
}
